import { useEffect, useState } from 'react';
import { supabase } from '../supabase';
import { useUserData } from '../auth';
import { normalizeWarehouseReporting, type WarehouseReportingSettings } from '../utils/warehouseReportingSettings';

/**
 * Preferenze magazzino per-organizzazione (colonna jsonb `settings.warehouse_settings`).
 *
 * Chiavi supportate:
 * - `inbound_flow_mode`: 'staged' (default) | 'direct'
 *   - staged: entrata merce in bozza/ricevuto, carico lotti via wizard dedicato
 *   - direct: il submit del form entrata esegue il carico contestuale e
 *     l'entrata nasce già CARICATA (spec Ideariso D20)
 * - `outbound_ddt_numbering`: true = numero dei DDT in uscita assegnato dal DB,
 *   progressivo nell'anno (trigger 20261001b); il form non lo fa scrivere
 * - `order_print_hide_unit_note`: true = la stampa dell'ordine (ManualOrderPrint)
 *   non ripete sotto l'articolo la nota automatica «UM: …». Richiesta Say Farm
 * - `commercial_name_on_documents`: true = preventivo, DDT in uscita e fattura
 *   usano il nome commerciale dell'articolo (l'ordine mostra entrambi)
 * - `outbound_returns_enabled`: true = resi da DDT in uscita («Registra reso» sul
 *   DDT spedito, elenco Resi in Spedizione; scrive flusia-core)
 * - `line_processing_notes_enabled`: true = «Note di lavorazione» sulle righe
 *   prodotto dell'offerta, lette dal magazzino (lib/deals/lineProcessingNotes.ts)
 * - `line_packaging_split_enabled`: true = «Confezionamento» sulle righe prodotto
 *   dell'offerta (es. 5 confezioni da 1 kg), letto dal magazzino e stampato
 *   nell'ordine (lib/deals/linePackaging.ts). Richiesta Say Farm
 * - `client_vat_codes_enabled`: true = codici IVA dei clienti (esenzioni) in
 *   impostazioni, scheda cliente e offerta (lib/services/vatCodeService.ts)
 * - `product_label_operational`: true = l'etichetta stampata dalla scheda
 *   articolo non ha il codice a barre ma informazioni operative: magazzino
 *   (uso interno) o nome commerciale (ordini), Lotto Campo e QR. Richiesta Say Farm
 * - `default_unit_of_measure`: unità di misura predefinita dell'org (es. 'g'),
 *   null = non impostata → form e visualizzazioni restano come prima ('pz').
 *   Vedi useOrgUnitOfMeasure.
 *
 * Stesso pattern cache module-level di useWarehouseFieldVisibility: una sola
 * query per sessione, invalidazione esplicita dopo il salvataggio settings.
 * select('*') così non fallisce se la colonna non esiste ancora (pre-migration
 * il default resta 'staged' = comportamento attuale).
 */

export type InboundFlowMode = 'staged' | 'direct';
export type DuplicateLotPolicy = 'ask' | 'merge' | 'separate';

export interface WarehouseSettings {
  inbound_flow_mode: InboundFlowMode;
  default_unit_of_measure: string | null;
  /** Stato degli ordini aggiornato dai DDT in uscita (lo applica flusia-core, qui solo lettura) */
  order_status_from_ddt: boolean;
  duplicate_lot_policy: DuplicateLotPolicy;
  outbound_require_tracked_lot: boolean;
  /** Numero dei DDT in uscita assegnato in automatico (progressivo nell'anno) */
  outbound_ddt_numbering: boolean;
  /** Stampa dell'ordine senza la nota automatica «UM: …» sotto l'articolo */
  order_print_hide_unit_note: boolean;
  /** Nome commerciale al posto di quello magazzino su preventivo, DDT in uscita e fattura */
  commercial_name_on_documents: boolean;
  /** Resi da DDT in uscita attivi */
  outbound_returns_enabled: boolean;
  /** Note di lavorazione per riga prodotto dell'offerta */
  line_processing_notes_enabled: boolean;
  /** Codici IVA dei clienti (esenzioni) */
  client_vat_codes_enabled: boolean;
  /** Etichetta articolo con informazioni operative al posto del codice a barre */
  product_label_operational: boolean;
  /** Confezionamento (n. confezioni × quantità) per riga prodotto dell'offerta */
  line_packaging_split_enabled: boolean;
  reporting: WarehouseReportingSettings;
}

const DEFAULTS: WarehouseSettings = { inbound_flow_mode: 'staged', default_unit_of_measure: null, order_status_from_ddt: false, duplicate_lot_policy: 'ask', outbound_require_tracked_lot: false, outbound_ddt_numbering: false, order_print_hide_unit_note: false, commercial_name_on_documents: false, outbound_returns_enabled: false, line_processing_notes_enabled: false, client_vat_codes_enabled: false, product_label_operational: false, line_packaging_split_enabled: false, reporting: normalizeWarehouseReporting(null) };

const cache = new Map<string, WarehouseSettings>();
const inflight = new Map<string, Promise<WarehouseSettings>>();
const listeners = new Set<() => void>();
let generation = 0;

async function loadSettings(organizationId: string): Promise<WarehouseSettings> {
  if (cache.has(organizationId)) return cache.get(organizationId)!;
  if (!inflight.has(organizationId)) {
    const started = generation;
    const request = (async () => {
      let value = { ...DEFAULTS };
      try {
        const { data, error } = await supabase.from('settings').select('*')
          .eq('organization_id', organizationId).order('updated_at', { ascending: false }).limit(1).maybeSingle();
        if (error) throw error;
        const raw = (data as Record<string, unknown> | null)?.warehouse_settings;
        const parsed = (raw && typeof raw === 'object' && !Array.isArray(raw))
          ? (raw as Record<string, unknown>)
          : {};
        value = {
          inbound_flow_mode: parsed.inbound_flow_mode === 'direct' ? 'direct' : 'staged',
          default_unit_of_measure:
            typeof parsed.default_unit_of_measure === 'string' && parsed.default_unit_of_measure.trim()
              ? parsed.default_unit_of_measure.trim()
              : null,
          order_status_from_ddt: parsed.order_status_from_ddt === true,
          duplicate_lot_policy: parsed.duplicate_lot_policy === 'merge' || parsed.duplicate_lot_policy === 'separate' ? parsed.duplicate_lot_policy : 'ask',
          outbound_require_tracked_lot: parsed.outbound_require_tracked_lot === true,
          outbound_ddt_numbering: parsed.outbound_ddt_numbering === true,
          order_print_hide_unit_note: parsed.order_print_hide_unit_note === true,
          commercial_name_on_documents: parsed.commercial_name_on_documents === true,
          outbound_returns_enabled: parsed.outbound_returns_enabled === true,
          line_processing_notes_enabled: parsed.line_processing_notes_enabled === true,
          client_vat_codes_enabled: parsed.client_vat_codes_enabled === true,
          product_label_operational: parsed.product_label_operational === true,
          line_packaging_split_enabled: parsed.line_packaging_split_enabled === true,
          reporting: normalizeWarehouseReporting(parsed.reporting),
        };
        if (started === generation) cache.set(organizationId, value);
      } catch { /* Keep safe defaults; an error is not cached across future mounts. */ }
      return value;
    })();
    inflight.set(organizationId, request);
    void request.finally(() => { if (inflight.get(organizationId) === request) inflight.delete(organizationId); });
  }
  return inflight.get(organizationId)!;
}

/** Forza il ricaricamento al prossimo uso (es. dopo un salvataggio settings). */
export function invalidateWarehouseSettings(): void {
  generation++;
  cache.clear();
  inflight.clear();
  listeners.forEach(listener => listener());
}

export function useWarehouseSettings() {
  const { organizationId } = useUserData();
  const [revision, setRevision] = useState(0);
  const [result, setResult] = useState<{ org: string; settings: WarehouseSettings } | null>(null);
  useEffect(() => {
    const refresh = () => { setResult(null); setRevision(value => value + 1); };
    listeners.add(refresh);
    return () => { listeners.delete(refresh); };
  }, []);

  useEffect(() => {
    let cancelled = false;
    if (!organizationId) return;
    loadSettings(organizationId).then((settings) => {
      if (cancelled) return;
      setResult({ org: organizationId, settings });
    });
    return () => {
      cancelled = true;
    };
  }, [organizationId, revision]);

  const settings = organizationId && (result?.org === organizationId ? result.settings : cache.get(organizationId));
  return { ...(settings || DEFAULTS), loaded: !!settings };
}
